<template>
  <OrganizationInvitationCard
    v-if="user.isKind('user') && account?.accountType === 'treezor' && !account.supervisorOrgId"
    class="mb-4"
    :account-id="accountId"
  />
  <div v-if="loading">
    <v-row>
      <v-col v-for="i in 4" :key="i" cols="6">
        <v-skeleton-loader type="image" />
      </v-col>
    </v-row>
  </div>
  <div
    v-else-if="
      account?.accountType === 'swan' &&
      (accountState?.status === 'pending' || accountState?.status === 'refused')
    "
    class="flex flex-col gap-8"
  >
    <div
      v-if="user.isKind('admin') && account.swan_kyb"
      class="rounded-lg border border-solid border-gray-200 p-4 dark:border-white/10"
    >
      <div class="text-xl font-bold">{{ account.name }}</div>
      <dl class="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
        <dt class="text-medium-emphasis">{{ t("swanKyb.header.swanName") }}</dt>
        <dd>{{ account.swan_kyb.companyName ?? "—" }}</dd>
        <dt class="text-medium-emphasis">{{ t("swanKyb.header.siren") }}</dt>
        <dd>{{ account.swan_kyb.registrationNumber ?? "—" }}</dd>
        <dt class="text-medium-emphasis">{{ t("swanKyb.header.reference") }}</dt>
        <dd class="flex items-center gap-1">
          <code>{{ account.swan_kyb.reference }}</code>
          <v-btn
            v-tooltip="t('swanKyb.header.copy')"
            icon="mdi-content-copy"
            size="x-small"
            variant="text"
            @click="copySwanReference"
          />
        </dd>
      </dl>
    </div>
    <SwanVerification
      v-if="
        user.isKind('admin') &&
        account.swan_kyb &&
        getSwanKybFormState(account.swan_kyb.form) === 'finished'
      "
      :kyb="account.swan_kyb"
    />
    <v-alert
      v-else-if="accountState.status === 'refused'"
      type="error"
      variant="tonal"
      :title="t('account.status.refused')"
      :text="t('swanKyb.refusedMessage')"
    />
    <SwanOnboardingProgress v-else :account-id="accountId" />
  </div>
  <div v-else-if="account && currencyCode">
    <v-row density="compact" class="items-stretch">
      <v-col class="flex" cols="4">
        <InformationsCard :account="account" :account-id="accountId" />
      </v-col>
      <v-col cols="8">
        <DashboardCard :title="t('charts.outcomesByCategory')">
          <template #subtitle>
            <div class="text-medium-emphasis text-xs flex items-center">
              {{ dateSubtitle }}
              <DatePicker v-slot="{ props: _props }" v-model="filteredDates">
                <v-btn v-bind="_props" density="compact" icon="mdi-chevron-down"></v-btn>
              </DatePicker>
            </div>
          </template>
          <CategoriesChart
            :data="categoriesChartData"
            :currency-code="currencyCode"
          ></CategoriesChart>
        </DashboardCard>
      </v-col>
      <v-col cols="12">
        <DashboardCard :title="t('charts.cashflow')">
          <BarChart :data="barChartData" :currency-code="currencyCode"></BarChart>
        </DashboardCard>
      </v-col>
    </v-row>
  </div>
  <v-row
    v-else-if="account?.accountType === 'treezor' && account.contractEnvelope"
    density="compact"
  >
    <v-col class="flex" cols="4">
      <InformationsCard :account="account" :account-id="accountId" />
    </v-col>
  </v-row>
  <div v-else>{{ t("common.noData") }}</div>
</template>

<script lang="ts" setup>
import BarChart from "@/components/BarChart.vue";
import CategoriesChart from "@/components/CategoriesChart.vue";
import DatePicker from "@/components/DatePicker.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency.ts";
import { useAccountState } from "@/composables/useAccountState";
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useTransactionCategories } from "@/composables/useTransactionCategories";
import { useTransactions } from "@/composables/useTransactions.ts";
import { db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import type { Account } from "@aircap/common/account";
import { getSwanKybFormState } from "@aircap/common/swan";
import { captureException } from "@sentry/vue";
import { collection, doc } from "firebase/firestore";
import { computed, toRef } from "vue";
import DashboardCard from "./components/DashboardCard.vue";
import InformationsCard from "./components/InformationsCard.vue";
import OrganizationInvitationCard from "./components/OrganizationInvitationCard.vue";
import SwanOnboardingProgress from "./components/SwanOnboardingProgress.vue";
import SwanVerification from "./components/SwanVerification.vue";
import { useCharts } from "./useCharts";

const { t } = useI18n();
const user = useUserStore();
const { showSuccessToast, showErrorToast } = useToast();
const props = defineProps<{
  accountId: string;
}>();

const accountSource = computed(() => doc(collection<Account>(db, "accounts"), props.accountId));
const { data: account, pending: accountLoading } = useDocument<Account>(accountSource);

const { accountState } = useAccountState(computed(() => account.value ?? undefined));

const { currencyCode } = useAccountCurrency(toRef(props.accountId));

const { transactions, loading: transactionsLoading } = useTransactions(toRef(props.accountId));

const { trackedCategories: trackedCategoriesRaw } = useTransactionCategories(
  toRef(() => props.accountId),
);
const trackedCategoriesForChart = computed(
  () => trackedCategoriesRaw.value?.map((c) => ({ id: c.id, name: c.name })) ?? [],
);

const { barChartData, categoriesChartData, dateSubtitle, filteredDates } = useCharts(
  transactions,
  trackedCategoriesForChart,
);

const copySwanReference = async () => {
  const reference = account.value?.swan_kyb?.reference;
  if (!reference) return;
  try {
    await navigator.clipboard.writeText(reference);
    showSuccessToast(t("swanKyb.header.copied"));
  } catch (cause) {
    const error = new Error(`Error copying Swan reference ${reference}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const loading = computed(() => accountLoading.value || transactionsLoading.value);
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 0px;
  z-index: 2;
}

thead th {
  position: sticky;
  background: rgb(var(--v-theme-surface));
  top: 0;
  z-index: 2;
}

:deep(.v-table__wrapper) {
  overflow: unset !important;
}
</style>
